import React, {useEffect, useState} from 'react';
import {Box, Typography, Checkbox, CircularProgress, Select, MenuItem} from '@mui/material';
import {useHistory, useLocation} from 'react-router-dom';
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from 'recharts';
import {NAVY, ORANGE, TEAL, StatCard} from '../../SchoolDashboard/components/widgets.comp';
import {getProgramImpactProgramsService, getProgramImpactDashboardService} from '../../../services/dynamicDashboard.service';
import {
    PROGRAM_IMPACT_SECTIONS, PASS_THRESHOLD, kpisFrom, classRowsFrom, programImpactMetaFrom,
    defaultDashboardFor, instituteOptionsFrom, programListHintFor, classifyDashboardError,
} from '../../DashboardCommon/programImpact';
import {ClassPassRateCard} from '../../DashboardCommon/ClassPassRateCard.comp';
import ScopeSuppressedBanner from '../../DashboardCommon/ScopeSuppressedBanner.comp';

const PRE_COLOR = '#A9B4D4';
const POST_COLOR = TEAL;

// A response without card sections reads as empty cards (same as the School page).
const NO_SECTIONS = {overviewCards: [], byClassCards: [], meta: null, classTable: null, schoolAvgPost: null};

/* Program Impact Dashboard for LC Teachers (level 23 + create_learning_program),
   served at /dq_pro/program-impact-dashboard and /dq_pro/lc-program-impact-dashboard.
   - the program list is GET program_impact/programs (the teacher's own programs);
   - the selected program's figures are its Program Impact dashboard (PID.S_1 / PID.S_2),
     read by module_code and, when chosen, institute_code.
   The card readers live in DashboardCommon/programImpact (shared with the School page). */
const InfoPill = ({children}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #fff)', px: 2, py: 1,
        fontSize: '13px', color: '#374151', whiteSpace: 'nowrap',
    }}>
        {children}
    </Box>
);

const StatusText = ({children, detail}) => (
    <Box sx={{py: 4}}>
        <Typography sx={{fontSize: '14px', color: '#6A7282'}}>{children}</Typography>
        {detail && <Typography sx={{fontSize: '12.5px', color: '#9CA3AF', mt: 0.5}}>{detail}</Typography>}
    </Box>
);

const ProgramImpactPage = () => {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const selectedCode = params.get('program_code') || null;
    // An empty ?institute_code= is the user's explicit "Default institute" choice;
    // a missing one means "use the program's default dashboard".
    const urlInstitute = params.has('institute_code') ? (params.get('institute_code') || '') : null;

    // instituteCode undefined/null → param left out; a string (even '') → kept so the choice sticks.
    const urlFor = (code, instituteCode) => `${location.pathname}?program_code=${encodeURIComponent(code)}`
        + (typeof instituteCode === 'string' ? `&institute_code=${encodeURIComponent(instituteCode)}` : '');

    // The teacher's own programs (handover §3). No sample fallback: an empty or
    // failed list shows its own state.
    const [list, setList] = useState({programs: [], loading: true, error: null});
    useEffect(() => {
        let cancelled = false;
        getProgramImpactProgramsService()
            .then((res) => {
                if (cancelled) return;
                const programs = res?.data?.programs || [];
                setList({programs, loading: false, error: null});
                // Fresh visit or a code that is not one of the teacher's programs — default to the first.
                if (programs.length && !programs.some((p) => p.code === selectedCode)) {
                    history.replace(urlFor(programs[0].code));
                }
            })
            .catch((err) => {
                if (!cancelled) setList({programs: [], loading: false, error: err});
            });
        return () => { cancelled = true; };
        // Load once on mount; selection changes only touch the URL.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {programs} = list;
    const selectedProgram = programs.find((p) => p.code === selectedCode) || null;
    // "" (the default institute) is never sent: the server treats it as omitted (handover §1).
    const instituteCode = urlInstitute !== null ? urlInstitute : (defaultDashboardFor(selectedProgram)?.institute_code || '');
    // Same reading as programListHintFor: a missing has_dashboard counts as "no dashboard".
    const hasDashboard = !!selectedProgram?.has_dashboard;
    // The request the body should show; null when there is nothing to fetch.
    const requestKey = selectedProgram && hasDashboard ? `${selectedProgram.code}|${instituteCode}` : null;

    // The selected program's Program Impact dashboard (handover §1).
    const [impactState, setImpact] = useState({key: null, data: null, availableInstitutes: [], loading: false, error: null});
    useEffect(() => {
        if (!requestKey) {
            setImpact({key: null, data: null, availableInstitutes: [], loading: false, error: null});
            return undefined;
        }
        let cancelled = false;
        setImpact({key: requestKey, data: null, availableInstitutes: [], loading: true, error: null});
        getProgramImpactDashboardService({
            module_code: selectedProgram.code,
            card_sections: PROGRAM_IMPACT_SECTIONS.join(','),
            ...(instituteCode ? {institute_code: instituteCode} : {}),
        })
            .then((res) => {
                if (!cancelled) setImpact({key: requestKey, data: res?.data || null, availableInstitutes: res?.available_institutes || [], loading: false, error: null});
            })
            .catch((err) => {
                if (!cancelled) setImpact({key: requestKey, data: null, availableInstitutes: [], loading: false, error: err});
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);
    // State left over from the previous selection is never drawn: until the effect for the
    // current selection has run, the body reads as loading (or empty when nothing is fetched).
    const impact = impactState.key === requestKey
        ? impactState
        : {key: requestKey, data: null, availableInstitutes: [], loading: requestKey !== null, error: null};

    // Single-select — picking a program replaces whichever was selected before,
    // and starts from that program's default institute.
    const selectProgram = (code) => {
        history.push(urlFor(code));
    };
    const selectInstitute = (code) => {
        history.push(urlFor(selectedProgram.code, code));
    };

    const instituteOptions = selectedProgram ? instituteOptionsFrom(selectedProgram, impact.availableInstitutes) : [];
    const selectedDashboard = (selectedProgram?.dashboards || []).find((d) => (d?.institute_code || '') === instituteCode)
        || defaultDashboardFor(selectedProgram);

    const {overviewCards, meta, classTable, schoolAvgPost} = programImpactMetaFrom(impact.data) || NO_SECTIONS;
    const stats = kpisFrom(overviewCards);
    const byClass = classRowsFrom(classTable);
    const chartData = byClass.filter((c) => !c.suppressed && (c.pre !== null || c.post !== null)).map((c) => ({grade: c.label, Pre: c.pre, Post: c.post}));
    const sessionsLabel = meta?.session_count
        ? `${meta.session_count} session${meta.session_count === 1 ? '' : 's'}${meta.minutes_per_session ? ` × ${meta.minutes_per_session} min` : ''}`
        : null;
    const learnersKnown = meta && (meta.learners != null || meta.classes_assigned != null || meta.classes_total != null);
    const showBody = !impact.loading && !impact.error && !!impact.data;
    const errorKind = impact.error ? classifyDashboardError(impact.error) : null;

    const title = selectedProgram ? `Program Impact: ${selectedProgram.name}` : 'Select a Program';

    return (
        <Box sx={{width: '100%', maxWidth: '100%', px: {xs: 2, sm: 0}, py: {xs: 3, sm: 4}}}>
            <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3}}>
                <Typography sx={{fontSize: {xs: '22px', md: '23px'}, fontWeight: 800, color: NAVY, lineHeight: 1.2}}>
                    {title}
                </Typography>
                {showBody && meta && !meta.signal && !meta.ec_band && meta.message && (
                    <Typography sx={{fontSize: '13px', color: '#6A7282'}}>{meta.message}</Typography>
                )}
            </Box>

            {showBody && <ScopeSuppressedBanner dashboardData={impact.data}/>}

            {showBody && (
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'}, gap: 2, mb: 3}}>
                    {stats.map((s) => (
                        <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub}/>
                    ))}
                </Box>
            )}
            <Box sx={{display: 'flex', mt: 7, flexDirection: {xs: 'column', md: 'row'}, gap: 2.5, alignItems: 'stretch'}}>
                <Box sx={{
                    width: {xs: '100%', md: '300px'}, flexShrink: 0,
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    p: 2.5, display: 'flex', flexDirection: 'column',
                }}>
                    <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                        Select a Program
                    </Typography>
                    <Typography sx={{fontSize: '12.5px', color: '#9CA3AF', mb: 1.5}}>
                        Choose one program to view its impact.
                    </Typography>
                    {list.loading && <CircularProgress size={20}/>}
                    {!list.loading && list.error && (
                        <Box>
                            <Typography sx={{fontSize: '13px', color: '#6A7282'}}>Could not load your programs.</Typography>
                            {list.error.message && <Typography sx={{fontSize: '12px', color: '#9CA3AF', mt: 0.5}}>{list.error.message}</Typography>}
                        </Box>
                    )}
                    {!list.loading && !list.error && programs.length === 0 && (
                        <Typography sx={{fontSize: '13px', color: '#9CA3AF'}}>
                            No deployed programs yet.
                        </Typography>
                    )}
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, overflowY: 'auto'}}>
                        {programs.map((p) => {
                            const active = selectedCode === p.code;
                            const hint = programListHintFor(p, active ? selectedDashboard : defaultDashboardFor(p));
                            return (
                                <Box
                                    key={p.code}
                                    onClick={() => selectProgram(p.code)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                                        borderRadius: '8px', px: 1, py: 0.25,
                                        backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'transparent',
                                        '&:hover': {backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'rgba(0, 0, 0, 0.03)'},
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={active}
                                        onChange={() => selectProgram(p.code)}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{color: ORANGE, p: 0.5, '&.Mui-checked': {color: ORANGE}}}
                                    />
                                    <Box sx={{minWidth: 0}}>
                                        <Typography sx={{fontSize: '13.5px', color: active ? NAVY : '#374151', fontWeight: active ? 700 : 400, lineHeight: 1.4}}>
                                            {p.name}
                                        </Typography>
                                        {hint && (
                                            <Typography sx={{fontSize: '12px', color: '#9CA3AF', lineHeight: 1.3}}>{hint}</Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column'}}>
                    {selectedProgram && instituteOptions.length > 1 && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5}}>
                            <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY}}>Institute</Typography>
                            <Select
                                size="small"
                                displayEmpty
                                value={instituteCode}
                                onChange={(e) => selectInstitute(e.target.value)}
                                sx={{fontSize: '13px', minWidth: 220, backgroundColor: 'var(--brand-surface, #fff)'}}
                            >
                                {instituteOptions.map((o) => (
                                    <MenuItem key={o.institute_code || 'default'} value={o.institute_code} sx={{fontSize: '13px'}}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {impact.loading && <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}><CircularProgress/></Box>}
                    {selectedProgram && !impact.loading && errorKind === 'rejected' && (
                        <StatusText detail={impact.error.message}>You do not have access to this program's dashboard.</StatusText>
                    )}
                    {selectedProgram && !impact.loading && errorKind === 'failed' && (
                        <StatusText detail={impact.error.message}>Impact data could not be loaded. Try again later.</StatusText>
                    )}
                    {selectedProgram && !impact.loading && !impact.error && !impact.data && (
                        <StatusText>Impact data for this program is not available yet.</StatusText>
                    )}

                    {showBody && (<>
                    {/* Program meta chips (PID.S_1.C_1); a chip without a value is left out. */}
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5}}>
                        {meta?.target_area_label && <InfoPill><strong>Target Area:</strong>&nbsp;{meta.target_area_label}</InfoPill>}
                        {sessionsLabel && <InfoPill>{sessionsLabel}</InfoPill>}
                        {learnersKnown && <InfoPill>{meta.learners ?? '—'} learners · {meta.classes_assigned ?? '—'} / {meta.classes_total ?? '—'} classes</InfoPill>}
                    </Box>

                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2.5, alignItems: 'stretch'}}>
                        {/* Pre → Post by class */}
                        <Box sx={{
                            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                            backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            p: 3, display: 'flex', flexDirection: 'column',
                        }}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                                Pre → Post by Class
                            </Typography>
                            <Typography sx={{fontSize: '13px', color: '#6A7282', mb: 2}}>
                                {schoolAvgPost !== null ? <>School avg post <strong style={{color: NAVY}}>{Math.round(schoolAvgPost)}</strong> · </> : null}{stats[2].value} impact
                            </Typography>
                            {!chartData.length && (
                                <Typography sx={{fontSize: '13px', color: '#6A7282'}}>No pre and post scores yet.</Typography>
                            )}
                            {chartData.length > 0 && <Box sx={{flexGrow: 1, minHeight: 260, width: '100%'}}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{top: 10, right: 10, left: -20, bottom: 0}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6"/>
                                        <XAxis dataKey="grade" tick={{fontSize: 11, fill: '#6B7280'}} stroke="#E5E7EB"/>
                                        <YAxis tick={{fontSize: 11, fill: '#6B7280'}} stroke="#E5E7EB"/>
                                        <Tooltip/>
                                        <Legend wrapperStyle={{fontSize: 12}}/>
                                        <Bar dataKey="Pre" fill={PRE_COLOR} radius={[4, 4, 0, 0]}/>
                                        <Bar dataKey="Post" fill={POST_COLOR} radius={[4, 4, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>}
                        </Box>

                        {/* Impact by class · pass rate */}
                        <ClassPassRateCard byClass={byClass}/>
                    </Box>

                    <Typography sx={{fontSize: '12px', color: '#9CA3AF', mt: 2.5, lineHeight: 1.6}}>
                        * Pass rate = share of learners scoring {PASS_THRESHOLD} or above on the post-assessment. Impact rate
                        = gap closure = (Post − Pre) ÷ (100 − Pre) × 100.
                    </Typography>
                    </>)}
                </Box>
            </Box>
        </Box>
    );
};

export default ProgramImpactPage;
